import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography"
import SaveIcon from "@mui/icons-material/Save";
import Select from '@mui/material/Select';
import CancelOutlined from "@mui/icons-material/Cancel";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { TicketFormContainer } from "../../Settings/Ticket/TicketFormPage";
import { FindAllByAlarmId, OpenTicket, UpdateTicket, CloseTicket } from "../../Settings/Ticket/TicketReactQuery";
import { Ticket } from "../../../Shared/Types/Ticket";
import { useAppSelector } from "../../../Config/Hooks";
import { hasPermission } from "../../../Shared/Auth/AuthenticationUtil";
import { AUTHORITIES } from "../../../Config/Constants";

type TicketModalProps = {
    ticketId?: number | null,
    alarm?: any | null,
    open: boolean,
    onClose: Function,
    canQueryTickets?: boolean,
};

export function TicketUpdateModal({ticketId, alarm, open, onClose, canQueryTickets = false}: TicketModalProps) {
    const account = useAppSelector(state => state.authentication.account);
    const isAuthorizedToSave = hasPermission(account?.authoritySet, [ AUTHORITIES.SYSADMIN, AUTHORITIES.UPDATE_TICKET ]);
    const isAuthorizedToDelete = hasPermission(account?.authoritySet, [ AUTHORITIES.SYSADMIN, AUTHORITIES.DELETE_TICKET ]);
    const activeAlarm = open ? alarm : null;

    const openTicket = OpenTicket();
    const updateTicket = UpdateTicket();
    const closeTicket = CloseTicket();
    const [ticket, setTicket] = useState<Ticket | null>(null);
    const [ticketIdSelected, setTicketIdSelected] = useState<string | null>(null);
    const {data: tickets} = FindAllByAlarmId(activeAlarm?.id, canQueryTickets, open);
    const [isTicketClosed, setIsTicketClosed] = useState<boolean>(false);

    const handleSave = () => {
        if (!ticket) return;

        const opts = {
            onSuccess: () => handleCloseModal()
        };
        if (ticket.id) {
            updateTicket.mutate(ticket, opts);
        } else {
            openTicket.mutate(ticket, opts);
        }
    };

    const handleCloseTicket = () => {
        if (!ticket?.id) return;
        const opts = {
            onSuccess: () => handleCloseModal()
        };
        closeTicket.mutate(ticket.id, opts);
    }

    const handleCloseModal = () => {
        ticketId = null;
        alarm = null;
        setTicket(null);
        setTicketIdSelected(null);
        onClose();
    };

    const [content, setContent] = useState<JSX.Element>();
    useEffect(() => {
        if (activeAlarm) {
            setContent(<TicketFormContainer ticketId={ticketIdSelected ? Number(ticketIdSelected) : null} alarm={activeAlarm} handleChange={setTicket} handleIsTicketClosed={setIsTicketClosed} />);
        } else {
            setContent(<TicketFormContainer ticketId={ticketId} handleChange={setTicket} handleIsTicketClosed={setIsTicketClosed} />);
        }
    }, [ticketId, activeAlarm, ticketIdSelected]);

    return (
        <Modal
            open={open}
            onClose={() => handleCloseModal()}
            aria-labelledby="modal-title"
            aria-describedby="modal-description"
        >
            <Card sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '80%',
                height: '80%',
                // bgcolor: 'background.paper',
                borderRadius: '8px',
                boxShadow: 24,
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto',
            }}>
                <CardHeader
                    sx={{ paddingBottom: 0 }}
                    title={
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                        }}>
                            <Typography variant="h6" component="div">
                                Ticket
                            </Typography>
                            {activeAlarm && (
                                <Box sx={{ width:300, marginLeft:5 }}>
                                    <FormControl fullWidth>
                                        <InputLabel id="ticketLabel">Selecione o Ticket</InputLabel>
                                        <Select
                                            labelId="ticketLabel"
                                            name="ticketIdSelected"
                                            value={ticketIdSelected || null}
                                            onChange={(e) => setTicketIdSelected(e.target.value)}
                                            variant={"standard"}
                                            label="Ticket"
                                        >
                                            {tickets?.data.map((ticket: Ticket) => (
                                              <MenuItem key={ticket.id} value={ticket.id}>
                                                {ticket.subject}
                                              </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            )}
                        </Box>
                    }
                />
                <CardContent>
                    {content != null && content}
                </CardContent>
                <CardActions sx={{
                    position: 'sticky',
                    bottom: 0,
                    padding: 2,
                    justifyContent: 'flex-end',
                    borderTop: '1px solid #e0e0e0',
                }}>
                    <Button
                        variant="contained"
                        size={"small"}
                        color={"primary"}
                        endIcon={<DeleteOutlineIcon />}
                        onClick={handleCloseTicket}
                        disabled={!isAuthorizedToDelete || (!ticket?.id || isTicketClosed)}
                    >
                        Finalizar
                    </Button>
                    <Button
                        variant="contained"
                        size={"small"}
                        color={"primary"}
                        endIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={isTicketClosed && !isAuthorizedToSave}
                    >
                        Salvar
                    </Button>
                    <Button
                        size={"small"}
                        variant="contained"
                        onClick={() => handleCloseModal()}
                        endIcon={<CancelOutlined />}
                        style={{marginLeft: 5}}
                    >
                        Cancelar
                    </Button>
                </CardActions>
            </Card>
        </Modal>
    );
}
